import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Popover, Typography, Button, Spin } from "antd";
import {
  BellOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  InboxOutlined,
  InfoCircleFilled,
  RightOutlined,
  WarningFilled,
} from "@ant-design/icons";
import { useNotifications } from "../../contexts/NotificationContext";
import { mapNotificationRow, THRESHOLD_EVENT_TYPE } from "../../utils/notifications";
import ThresholdConfigModal from "./ThresholdConfigModal";
import NotificationDetailModal from "./NotificationDetailModal";
import "../../styles/Notifications/Notifications.css";

const { Text } = Typography;

const TAB_PARAMS = {
  all: {},
  unread: { unread_only: true },
  threshold: { event_type: THRESHOLD_EVENT_TYPE },
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const {
    unreadCount,
    connectStream,
    disconnectStream,
    subscribe,
    fetchList,
    markRead,
    markUnread,
    markAllRead,
    dismissAll,
  } = useNotifications();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all"); // 'all' | 'unread' | 'threshold'
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [allTotal, setAllTotal] = useState(0);
  const [thresholdTotal, setThresholdTotal] = useState(0);
  const requestIdRef = useRef(0);

  const refreshCounts = useCallback(async () => {
    const [allRes, thresholdRes] = await Promise.all([
      fetchList({ page: 1, page_size: 1 }),
      fetchList({ page: 1, page_size: 1, event_type: THRESHOLD_EVENT_TYPE }),
    ]);
    if (allRes?.data?.status) setAllTotal(allRes.data.total || 0);
    if (thresholdRes?.data?.status) setThresholdTotal(thresholdRes.data.total || 0);
  }, [fetchList]);

  // silent = background refresh: keep current list on screen, swap data when ready.
  // Full loader only for first load / tab switch.
  const loadTab = useCallback(
    async (tab, { silent = false } = {}) => {
      const requestId = ++requestIdRef.current;
      if (!silent) setLoading(true);
      try {
        const res = await fetchList({ page: 1, page_size: 5, ...TAB_PARAMS[tab] });
        if (requestId !== requestIdRef.current) return; // stale response
        if (res?.data?.status) {
          setItems((res.data.data || []).map(mapNotificationRow));
        }
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [fetchList]
  );

  useEffect(() => {
    connectStream();
    refreshCounts();
    return () => disconnectStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTab(activeTab);
  }, [activeTab, loadTab]);

  useEffect(() => {
    return subscribe(() => {
      refreshCounts();
      loadTab(activeTab, { silent: true });
    });
  }, [subscribe, refreshCounts, loadTab, activeTab]);

  const [markingAll, setMarkingAll] = useState(false);
  const markingAllRef = useRef(false);

  const handleMarkAllRead = useCallback(async () => {
    if (markingAllRef.current) return;
    markingAllRef.current = true;
    setMarkingAll(true);
    try {
      await markAllRead();
    } catch (err) {
      markingAllRef.current = false;
      setMarkingAll(false);
    }
  }, [markAllRead]);

  // Stay disabled after success; re-enable once unread count reaches 0
  // so a later batch of new notifications can be marked again.
  useEffect(() => {
    if (unreadCount === 0) {
      markingAllRef.current = false;
      setMarkingAll(false);
    }
  }, [unreadCount]);

  const handleMarkRead = (id) => markRead([id]);
  const handleToggleRead = (item) =>
    item.read ? markUnread([item.id]) : markRead([item.id]);

  const handleCardClick = (item) => {
    if (!item.read) handleMarkRead(item.id);
    setSelectedNotif(item);
    setPopoverOpen(false);
  };

  const handleViewAll = () => {
    setPopoverOpen(false);
    navigate("/notifications");
  };

  const renderIcon = (item) => {
    if (!item.isThreshold) return <InfoCircleFilled />;
    return item.severity === "critical" ? (
      <ExclamationCircleFilled />
    ) : (
      <WarningFilled />
    );
  };

  const popoverContent = (
    <div className="notif_popover_content">
      {/* Header */}
      <div className="notif_panel_header">
        <div className="notif_panel_title_group">
          <Text className="notif_panel_title">Notifications</Text>
          {unreadCount > 0 && (
            <span className="notif_panel_unread_tag">{unreadCount} new</span>
          )}
        </div>
        <div className="notif_panel_actions">
          {unreadCount > 0 && (
            <Button
              type="link"
              className="notif_action_btn"
              onClick={handleMarkAllRead}
              disabled={markingAll}
            >
              Mark all read
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="notif_tabs_bar">
        <button
          className={`notif_tab_pill ${
            activeTab === "all" ? "notif_tab_pill--active" : ""
          }`}
          onClick={() => setActiveTab("all")}
        >
          All ({allTotal})
        </button>
        <button
          className={`notif_tab_pill ${
            activeTab === "unread" ? "notif_tab_pill--active" : ""
          }`}
          onClick={() => setActiveTab("unread")}
        >
          Unread ({unreadCount})
        </button>
        <button
          className={`notif_tab_pill ${
            activeTab === "threshold" ? "notif_tab_pill--active" : ""
          }`}
          onClick={() => setActiveTab("threshold")}
        >
          Defect Count Alerts ({thresholdTotal})
        </button>
      </div>

      {/* List - Latest 5 Only */}
      <div className="notif_list_container">
        {loading ? (
          <div className="notif_empty_state notif_loading_state">
            <Spin size="default" />
          </div>
        ) : items.length === 0 ? (
          <div className="notif_empty_state">
            <InboxOutlined className="notif_empty_icon" />
            <div className="notif_empty_text">
              {activeTab === "unread"
                ? "No unread notifications"
                : "No notifications found"}
            </div>
          </div>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              className={`notif_card ${!item.read ? "notif_card--unread" : ""}`}
              onClick={() => handleCardClick(item)}
            >
              <div
                className={`notif_icon_circle ${
                  item.severity === "critical"
                    ? "notif_icon_circle--critical"
                    : item.severity === "warning"
                    ? "notif_icon_circle--warning"
                    : "notif_icon_circle--info"
                }`}
              >
                {renderIcon(item)}
              </div>

              <div className="notif_card_content">
                <div className="notif_card_top">
                  <span className="notif_spec_tag">
                    {item.isThreshold ? `Spec ${item.spec}` : item.eventTypeLabel}
                  </span>
                  <span className="notif_time_group">
                    <span className="notif_time">{item.timestamp}</span>
                    {!item.read && <span className="notif_unread_dot" />}
                  </span>
                </div>

                <span className="notif_message">{item.message}</span>

                {item.isThreshold && (
                  <div className="notif_metrics_pill_row">
                    <span className="notif_threshold_pill">
                      Defects: {item.defectCount} · Rate {item.currentRate} vs baseline{" "}
                      {item.baselineRate} (+{item.thresholdPct}% threshold)
                    </span>
                    <span className="notif_model_pill">{item.flightModel}</span>
                  </div>
                )}

                <div
                  className="notif_card_actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    type="link"
                    className="notif_card_action_btn"
                    icon={<EyeOutlined />}
                    onClick={() => {
                      if (!item.read) handleMarkRead(item.id);
                      setSelectedNotif(item);
                      setPopoverOpen(false);
                    }}
                  >
                    Details
                  </Button>
                  <Button
                    type="link"
                    className="notif_card_action_btn"
                    onClick={() => handleToggleRead(item)}
                  >
                    {item.read ? "Mark unread" : "Mark read"}
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div
        className="notif_panel_footer"
        style={{ flexDirection: "column", gap: 8, padding: "10px 14px" }}
      >
        {allTotal > 0 && (
          <Button
            type="primary"
            block
            icon={<RightOutlined />}
            style={{
              height: 36,
              borderRadius: 6,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              backgroundColor: "#2563eb",
            }}
            onClick={handleViewAll}
          >
            View All Notifications ({allTotal})
          </Button>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            width: "100%",
            alignItems: "center",
          }}
        >
          {allTotal > 0 && (
            <Button type="text" danger size="small" style={{ fontSize: 11 }} onClick={dismissAll}>
              Clear all
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Popover
        content={popoverContent}
        trigger="click"
        open={popoverOpen}
        onOpenChange={(open) => {
          setPopoverOpen(open);
          if (open) {
            refreshCounts();
            loadTab(activeTab, { silent: items.length > 0 });
          }
        }}
        placement="bottomRight"
        overlayClassName="notif_popover"
      >
        <div className="notif_bell_trigger" title="Notifications">
          <Badge count={unreadCount} overflowCount={99} className="notif_bell_badge">
            <BellOutlined className="notif_bell_icon" />
          </Badge>
        </div>
      </Popover>

      <ThresholdConfigModal open={isConfigOpen} onClose={() => setIsConfigOpen(false)} />

      <NotificationDetailModal
        open={Boolean(selectedNotif)}
        notification={selectedNotif}
        onClose={() => setSelectedNotif(null)}
      />
    </>
  );
}
